import MuiLink from '@mui/material/Link';
import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import {
  Typography,
  Box,
  Modal,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Grid,
  FormControl,
  Stack,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { PersonOutlined, AccessTime, LocationOn, Phone } from '@mui/icons-material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import QrCodeIcon from '@mui/icons-material/QrCode';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { Container } from '@mui/system';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { clockForTimezone } from '../../util/clockSystem';
import TimezoneSelector from '../../util/timezoneSelector';
import { timezoneDisplayName } from '../../util/timezoneOptions';
import { DescriptionComponent, InfoRow, formatDuration } from '../../util/utils';
import { MonthYearSelector } from '../../../products/schedulingBookings/components/business/calendar/month/monthYearSelector';
import { UserContext } from '../../components/auth/auth-context';
import { useSelectedBusiness } from '../../redux/store';
import { CalendarProvider, useCalendar } from '../../../products/schedulingBookings/components/business/calendar/calendarContext';
import BookingTimeSlotContainer from '../../../products/schedulingBookings/components/business/calendar/week/bookingTimeSlotContainer';
import CalendarSystemSelector from '../../../products/schedulingBookings/components/business/calendar/calendarSystemSelector';
import { QRCodeCanvas } from 'qrcode.react';
import { rootClientPath } from '../../util/link';
import { getBrowserTimeZone, resolveTimeZone } from "../../util/timezoneUtils";
import { GET_STAFF_BY_URL } from '../../graphql/queries';

// Modal style
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '80vh',
  overflow: 'auto',
};

function normalizeToLocalDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0, 0);
  }
  const raw = String(value).trim();
  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]) - 1;
    const day = Number(dateOnlyMatch[3]);
    return new Date(year, month, day, 12, 0, 0, 0);
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate(), 12, 0, 0, 0);
}

const AppointmentBookingContent = ({
  selectedBookingData,
  selectedBookingDay,
  selectedBookingMonth,
  businessUrl,
  businessData,
}) => {
  const { bookingTypeLink } = useParams();
  const { providerUser } = useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const effectiveBusiness = useMemo(() => {
    if (businessData?.id) return businessData;
    return selectedBusiness || null;
  }, [businessData, selectedBusiness]);
  const fallbackBusinessStaffs = useMemo(
    () =>
      (effectiveBusiness?.staffs || effectiveBusiness?.businessStaffs || [])
        .filter((staff) => !!staff?.url)
        .map((staff) => ({
          staffId: Number(staff?.id),
          url: staff?.url,
          name: staff?.name || staff?.user?.username || "Provider",
          image: staff?.image || staff?.user?.image || null,
          user: staff?.user || null,
          business: staff?.business || effectiveBusiness || null,
        })),
    [effectiveBusiness]
  );
  const assigned = useMemo(() => {
    const directAssigned = Array.isArray(selectedBookingData?.assignedStaffs)
      ? selectedBookingData.assignedStaffs.filter((staff) => !!staff?.url)
      : [];
    return directAssigned.length > 0 ? directAssigned : fallbackBusinessStaffs;
  }, [fallbackBusinessStaffs, selectedBookingData?.assignedStaffs]);

  // pick a random staff (if any)
  const randomStaffId =
    assigned.length > 0
      ? assigned[Math.floor(Math.random() * assigned.length)]?.staffId
      : null;
  const businessScheduleTimeZone =
    effectiveBusiness?.workingHourGroups?.find((group) => group?.def)?.timeZone?.timezone ||
    effectiveBusiness?.workingHourGroups?.[0]?.timeZone?.timezone ||
    null;

  const [viewType, setViewType] = useState('month');
  const [showQRCode, setShowQRCode] = useState(false);

  const [selectedStaffId, setSelectedStaffId] = useState(randomStaffId);
  const [openModal, setOpenModal] = useState(false);

  const { setCurrentMonth, setSelectedDate, displayClock: preferredClock } = useCalendar();
  const timezoneChosen = useRef(false);
  const [selectedTimeZone, setSelectedTimeZone] = useState(
    resolveTimeZone(
      getBrowserTimeZone(),
      providerUser?.timeZone?.timezone,
      businessScheduleTimeZone
    )
  );

  useEffect(() => {
    if (timezoneChosen.current) return;
    setSelectedTimeZone(
      resolveTimeZone(
        getBrowserTimeZone(),
        providerUser?.timeZone?.timezone,
        businessScheduleTimeZone
      )
    );
  }, [businessScheduleTimeZone, providerUser?.timeZone?.timezone]);

  // Sync selected date/month from props safely
  useEffect(() => {
    const normalizedDay = normalizeToLocalDate(selectedBookingDay);
    if (normalizedDay) {
      setSelectedDate(normalizedDay);
    }
  }, [selectedBookingDay, setSelectedDate]);

  useEffect(() => {
    const normalizedMonth = normalizeToLocalDate(selectedBookingMonth);
    if (normalizedMonth) {
      setCurrentMonth(normalizedMonth);
    }
  }, [selectedBookingMonth, setCurrentMonth]);

  // If booking type changes (or staff list changes), initialize staff
  useEffect(() => {
    if (assigned.length === 0) {
      setSelectedStaffId(null);
    } else if (!assigned.some(s => s?.staffId === selectedStaffId)) {
      setSelectedStaffId(randomStaffId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBookingData?.id, assigned.length]);

  const displayClock = clockForTimezone(preferredClock, selectedTimeZone);

  const handleTimezoneSelect = useCallback((timezone) => {
    timezoneChosen.current = true;
    setSelectedTimeZone(resolveTimeZone(timezone));
  }, []);

  const handleStaffSelect = async (staffId) => {
    setSelectedStaffId(staffId);
    setOpenModal(false);
  };
  const getStaffImageUrl = (staff) => staff?.image?.url || staff?.user?.image?.url || "";
  const getStaffDisplayName = (staff) => {
    const rawName = String(staff?.name || "").trim();
    if (rawName && rawName !== "0" && rawName.toLowerCase() !== "null" && rawName.toLowerCase() !== "undefined") {
      return rawName;
    }
    const username = String(staff?.user?.username || "").trim();
    return username || "Provider";
  };

  const enrichedAssigned = useMemo(() => {
    const staffById = new Map(
      (effectiveBusiness?.businessStaffs || effectiveBusiness?.staffs || []).map((staff) => [Number(staff?.id), staff])
    );
    return assigned.map((staff) => {
      const fullStaff = staffById.get(Number(staff?.staffId));
      if (!fullStaff) return staff;
      return {
        ...fullStaff,
        ...staff,
        image: fullStaff?.image || staff?.image,
        user: fullStaff?.user || staff?.user,
        name: staff?.name || fullStaff?.user?.username || fullStaff?.name,
      };
    });
  }, [assigned, effectiveBusiness]);

  // Find the selected staff (only if a staff is chosen and exists)
  const selectedStaff =
    selectedStaffId && selectedStaffId !== 'All'
      ? enrichedAssigned.find((s) => Number(s.staffId) === Number(selectedStaffId))
      : null;
  const selectedAssignedStaff = useMemo(
    () => assigned.find((s) => Number(s?.staffId) === Number(selectedStaffId)) || assigned[0] || null,
    [assigned, selectedStaffId]
  );
  const activeStaffUrl = selectedAssignedStaff?.url || null;
  const { data: staffByUrlData } = useQuery(GET_STAFF_BY_URL, {
    variables: { staffUrl: activeStaffUrl, businessUrl },
    skip: !activeStaffUrl || !businessUrl,
  });
  const selectedStaffForDisplay = useMemo(() => {
    const staffByUrl = staffByUrlData?.staffByUrl;
    if (!selectedStaff) return staffByUrl || null;
    if (!staffByUrl) return selectedStaff;
    if (Number(staffByUrl?.id) !== Number(selectedStaff?.staffId)) {
      return {
        ...selectedStaff,
        image: selectedStaff?.image || staffByUrl?.image,
        user: selectedStaff?.user || staffByUrl?.user,
      };
    }
    return {
      ...selectedStaff,
      image: selectedStaff?.image || staffByUrl?.image,
      user: selectedStaff?.user || staffByUrl?.user,
    };
  }, [selectedStaff, staffByUrlData]);

  // Booking page URL for QR code
  const bookingPageUrl = `${rootClientPath}/${businessUrl}/${bookingTypeLink}`;

  const toggleQRCode = () => {
    setShowQRCode((prev) => !prev);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        maxWidth: 840,
        margin: '0 auto',
        padding: { xs: 0.25, sm: 0.5 },
        backgroundColor: 'transparent',
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <Box
        sx={{
          maxWidth: 840,
          margin: '0 auto',
          padding: { xs: 1.25, sm: 1.75 },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderRadius: 3,
          border: 1, borderColor: "divider",
          boxShadow: '0 18px 36px rgba(15, 31, 46, 0.08)',
        }}
      >
        {/* Provider and Company Information */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            p: { xs: 1.1, sm: 1.25 },
            borderRadius: 2.5,
            border: 1, borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', sm: 'flex-start' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            {selectedStaffForDisplay ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar src={getStaffImageUrl(selectedStaffForDisplay)} alt={getStaffDisplayName(selectedStaffForDisplay)} />
                <Typography variant="subtitle1">{getStaffDisplayName(selectedStaffForDisplay)}</Typography>
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Round-robin assignment
              </Typography>
            )}

            {selectedStaffForDisplay?.address && (
              <Typography variant="body2" color="text.secondary">
                {selectedStaffForDisplay.address}
              </Typography>
            )}

            {enrichedAssigned.length > 1 && (
              <Button
                variant="outlined"
                onClick={() => setOpenModal(true)}
                startIcon={<PersonOutlined />}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 1.75,
                  py: 0.5,
                  mt: 0.75,
                }}
              >
                Change
              </Button>
            )}
          </Box>

          {selectedStaffForDisplay?.business?.name && (
            <Link
              to={`/${businessUrl}`}
              underline="none"
              style={{ textDecoration: 'none' }}
            >
              <Typography
                sx={{ color: 'primary.main', textAlign: { xs: 'center', sm: 'right' } }}
              >
                {selectedStaffForDisplay.business.name}
              </Typography>
            </Link>
          )}
        </Box>

        {/* Main Content */}
        <Grid container spacing={2}>
          {/* Title and Details */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: { xs: 1.1, sm: 1.25 },
                borderRadius: 2.5,
                border: 1, borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                fontSize: { xs: '1.05rem', sm: '1.4rem' },
                textAlign: 'left',
                mb: 1,
                overflowWrap: 'break-word',
                color: "text.primary",
              }}
            >
              {selectedBookingData?.title}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <InfoRow icon={<AccessTime />} text={formatDuration(selectedBookingData?.length)} />

              {selectedBookingData?.location && (
                <InfoRow icon={<LocationOn />} text={selectedBookingData?.location} />
              )}

              {Number(selectedBookingData?.meetingLocationTypeId) === 2 && (
                <InfoRow
                  icon={<Phone />}
                  text={selectedBookingData.canInviteeCall
                    ? (selectedBookingData.phoneNumber?.trim()
                      ? <>Call <MuiLink href={`tel:${selectedBookingData.phoneNumber.replace(/[^+\d]/g, "")}`} color="text.primary" underline="always" sx={{ fontWeight: 500, textUnderlineOffset: "3px", textDecorationColor: "text.secondary", overflowWrap: "anywhere", "&:hover": { textDecorationColor: "currentColor" }, "&:focus-visible": { outline: "2px solid currentColor", outlineOffset: "3px", borderRadius: "2px" } }}>{selectedBookingData.phoneNumber}</MuiLink> at the scheduled time.</>
                      : "Contact the host for the phone number.")
                    : "We'll call you at the phone number you provide."}
                />
              )}

              {selectedBookingData?.price !== undefined &&
                selectedBookingData?.price !== null &&
                String(selectedBookingData?.price).trim() !== "" && (
                  <InfoRow
                    icon={<AttachMoneyIcon />}
                    text={Number(selectedBookingData?.price) === 0 ? "Free" : `$ ${selectedBookingData?.price}`}
                  />
                )}
            </Box>
            </Box>
          </Grid>

          {/* Description */}
          {selectedBookingData?.desc && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  p: { xs: 1.1, sm: 1.25 },
                  borderRadius: 2.5,
                  border: 1, borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              >
                <EventNoteIcon sx={{ fontSize: 20, mr: 1 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <DescriptionComponent
                    selectedBookingData={{
                      desc: selectedBookingData.desc,
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>

      <Container sx={{ display: 'flex', flexDirection: 'column', width: '100%', px: '0 !important', mt: 1, gap: 1.5 }}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            pr: 0,
            p: 0,
            gap: 1,
          }}
        >
          {/* Month/Year selector and Timezone */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              p: { xs: 1.1, sm: 1.25 },
              borderRadius: 2.5,
              border: 1, borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Choose a date</Typography>
              <ToggleButtonGroup
                value={viewType}
                exclusive
                size="small"
                aria-label="Calendar view"
                onChange={(_, value) => { if (value) setViewType(value); }}
                sx={{ flexShrink: 0 }}
              >
                <ToggleButton value="month" sx={{ textTransform: 'none', px: 1.5 }}>Month</ToggleButton>
                <ToggleButton value="week" sx={{ textTransform: 'none', px: 1.5 }}>Week</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <MonthYearSelector inline checkavailability={true} bookingType={selectedBookingData} timezone={selectedTimeZone} />

          </Box>
        </Box>

        {/* Time slots */}
        <Box
          sx={{
            p: { xs: 1, sm: 1.25 },
            borderRadius: 2.5,
            border: 1, borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Choose a Time
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {timezoneDisplayName(selectedTimeZone)} &middot; {displayClock === 'both' ? 'Ethiopian Day/Night + AM/PM' : displayClock === 'ethiopian' ? 'Ethiopian Day/Night' : 'AM/PM'}
              </Typography>
            </Box>

            <Stack spacing={0.5} sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
            <FormControl
              sx={{
                justifyContent: 'center',
                minWidth: { xs: 0, sm: 220 },
                width: '100%',
                '& .MuiInputBase-root': {
                  fontSize: '0.8rem',
                  fontWeight: 'normal',
                  cursor: 'pointer',
                  transition: 'border-color 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            >
              <TimezoneSelector timezoneId={selectedTimeZone} onTimezoneSelect={handleTimezoneSelect} />
            </FormControl>
            <CalendarSystemSelector timezone={selectedTimeZone} showTimezone={false} onTimezoneSelect={handleTimezoneSelect} />
            </Stack>
          </Stack>
          <BookingTimeSlotContainer
            isTimeSlotVisible={true}
            bookingType={selectedBookingData}
            selectedStaffId={selectedStaffId} // can be null (agent-set appt without staff)
            userTimezone={selectedTimeZone}
            viewType={viewType}
            businessUrl={businessUrl}
          />
        </Box>
        <Box sx={{ pt: 1.5, textAlign: 'center' }}>
          <Button size="small" color="inherit" startIcon={<QrCodeIcon />} onClick={toggleQRCode}
            aria-expanded={showQRCode} aria-controls="booking-share-qr"
            sx={{ color: 'text.secondary', textTransform: 'none' }}>
            {showQRCode ? 'Hide QR code' : 'Share booking QR code'}
          </Button>
          <Collapse in={showQRCode} unmountOnExit>
            <Box id="booking-share-qr" sx={{ pt: 1, pb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Scan to open this booking page</Typography>
              <Box sx={{ display: 'inline-flex', p: 1.5, bgcolor: '#fff', borderRadius: 2 }}>
                <QRCodeCanvas value={bookingPageUrl} size={128} />
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Container>

      {/* Staff selection modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Select Staff
          </Typography>
          <List>
            <ListItem button onClick={() => handleStaffSelect('All')} selected={selectedStaffId === 'All'}>
              <ListItemText primary="All Staff (Round-robin)" />
            </ListItem>

            {enrichedAssigned.map((staff) => (
              <ListItem
                key={staff?.id ?? staff?.staffId}
                button
                onClick={() => handleStaffSelect(staff.staffId)}
                selected={selectedStaffId === staff.staffId}
              >
                <ListItemAvatar>
                  <Avatar src={getStaffImageUrl(staff)} alt={getStaffDisplayName(staff)} />
                </ListItemAvatar>
                <ListItemText primary={getStaffDisplayName(staff)} secondary={staff?.address} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Modal>
    </Box>
  );
};

const AppointmentBooking = (props) => {
  return (
    <CalendarProvider initialCalendar={props.selectedBookingData?.calendarSystem || 'gregory'}>
      <AppointmentBookingContent {...props} />
    </CalendarProvider>
  );
};

export default AppointmentBooking;
